import {
  InfiniteData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/vue-query";
import { computed, type MaybeRef, unref } from "vue";

import type { components as OpenAPI } from "@/autogen/openapi";
import { ok } from "@/lib/openapi";

import api from "../../base";
import { useNewsletterId } from "../utils";

import { ExternalFeedPage } from "./list";

export type ExternalFeed = OpenAPI["schemas"]["ExternalFeed"];

export type ExternalFeedCreateInput = OpenAPI["schemas"]["ExternalFeedInput"];
export type ExternalFeedUpdateInput =
  OpenAPI["schemas"]["ExternalFeedUpdateInput"] & { id: string };

export const useExternalFeedDetailQuery = ({
  id,
}: {
  id: MaybeRef<string | undefined>;
}) => {
  const queryClient = useQueryClient();
  const newsletterId = useNewsletterId();

  const query = useQuery({
    queryKey: [newsletterId, "feeds", "detail", id],
    enabled: () => unref(id) !== undefined,
    async queryFn({ signal }): Promise<ExternalFeed> {
      const data = await ok(
        api.get("/external_feeds/{id}", {
          signal,
          params: {
            path: {
              id: unref(id)!,
            },
          },
        })
      );

      return data;
    },
  });

  const data = query.data;
  const dataWithFallback = computed(() => {
    if (data.value) {
      return data.value;
    }

    const needle = unref(id);
    const queries = queryClient.getQueriesData<InfiniteData<ExternalFeedPage>>({
      queryKey: [newsletterId, "feeds", "list"],
    });

    for (const [, data] of queries) {
      if (!data) {
        continue;
      }

      for (const page of data.pages) {
        for (const item of page.results) {
          if (item.id === needle) {
            return item satisfies ExternalFeed;
          }
        }
      }
    }

    return undefined;
  });

  return { ...query, dataWithFallback };
};

export const useExternalFeedDigestQuery = ({
  id,
}: {
  id: MaybeRef<string>;
}) => {
  const newsletterId = useNewsletterId();

  const query = useQuery({
    queryKey: [newsletterId, "feeds", "digest", id],
    async queryFn({ signal }) {
      const data = await ok(
        api.get("/external_feeds/{id}/items", {
          signal,
          params: {
            path: {
              id: unref(id),
            },
            query: {},
          },
        })
      );

      return data.results;
    },
  });

  return query;
};

export const useCreateExternalFeedMutation = () => {
  const queryClient = useQueryClient();
  const newsletterId = useNewsletterId();

  const mutation = useMutation({
    async mutationFn(input: ExternalFeedCreateInput): Promise<ExternalFeed> {
      const data = await ok(
        api.post("/external_feeds", {
          body: input,
        })
      );

      return data;
    },
    onSuccess(data) {
      queryClient.setQueryData(
        [newsletterId, "feeds", "detail", data.id],
        data
      );

      queryClient.invalidateQueries({
        queryKey: [newsletterId, "feeds", "list"],
      });
    },
  });

  return mutation;
};

export const useUpdateExternalFeedMutation = () => {
  const queryClient = useQueryClient();
  const newsletterId = useNewsletterId();

  const mutation = useMutation({
    async mutationFn({
      id,
      ...input
    }: ExternalFeedUpdateInput): Promise<ExternalFeed> {
      const data = await ok(
        api.patch("/external_feeds/{id}", {
          params: {
            path: { id },
          },
          body: input,
        })
      );

      return data;
    },
    onSuccess(data) {
      queryClient.setQueryData(
        [newsletterId, "feeds", "detail", data.id],
        data
      );

      queryClient.invalidateQueries({
        queryKey: [newsletterId, "feeds", "list"],
      });
    },
  });

  return mutation;
};

export const useDeleteExternalFeedMutation = () => {
  const queryClient = useQueryClient();
  const newsletterId = useNewsletterId();

  const mutation = useMutation({
    async mutationFn({ id }: { id: string }): Promise<void> {
      await ok(
        api.delete("/external_feeds/{id}", {
          params: {
            path: { id },
          },
        })
      );
    },
    onSuccess(_data, { id }) {
      queryClient.resetQueries({
        queryKey: [newsletterId, "feeds", "detail", id],
      });

      queryClient.invalidateQueries({
        queryKey: [newsletterId, "feeds", "list"],
      });
    },
  });

  return mutation;
};
